import { useRef, useEffect } from 'react';
import { useNexusStore } from '@/stores/nexusStore';
import { ScrollArea } from '@/components/ui/scroll-area';
import { StatusDot } from '@/components/shared/StatusDot';
import { Activity } from 'lucide-react';

const SEVERITY_COLORS = {
  critical: 'text-[var(--nexus-danger)]',
  warning: 'text-[var(--nexus-warning)]',
  success: 'text-[var(--nexus-success)]',
  info: 'text-[var(--nexus-accent)]',
};

const SEVERITY_BADGES = {
  critical: 'badge-p1',
  warning: 'badge-p2',
  success: 'badge-p3',
  info: 'badge-p4',
};

const formatTime = (timestamp) => {
  try {
    const d = new Date(timestamp);
    return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return '--:--:--';
  }
};

export const LiveActivityFeed = () => {
  const activityFeed = useNexusStore((s) => s.activityFeed);
  const feedEndRef = useRef(null);
  const scrollRef = useRef(null);

  // Auto-scroll to top when new entries arrive (newest first)
  useEffect(() => {
    if (scrollRef.current) {
      const viewport = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (viewport) {
        viewport.scrollTop = 0;
      }
    }
  }, [activityFeed.length]);

  return (
    <div data-testid="activity-feed" className="nexus-card relative overflow-hidden flex-1">
      <div className="p-3 border-b border-[var(--nexus-border)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity size={14} className="text-[var(--nexus-accent)]" />
            <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)] tracking-wide">Live Activity</h2>
          </div>
          <div className="flex items-center gap-1.5">
            <StatusDot status="live" pulse size="sm" />
            <span className="font-mono text-[10px] text-[var(--nexus-accent)]">STREAMING</span>
          </div>
        </div>
      </div>
      <ScrollArea className="h-[420px]" ref={scrollRef}>
        <div className="p-2 flex flex-col gap-1">
          {activityFeed.map((entry, idx) => (
            <div
              key={entry.id || idx}
              className={`p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] hover:bg-white/[0.02] transition-colors duration-100 ${
                idx === 0 ? 'animate-nexus-slide-in' : ''
              }`}
            >
              <div className="flex items-start gap-2">
                <StatusDot status={entry.severity || 'info'} size="sm" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className={`font-mono text-[10px] font-semibold ${SEVERITY_COLORS[entry.severity] || 'text-[var(--nexus-accent)]'}`}>
                      {entry.agent_name}
                    </span>
                    <span className="font-mono text-[10px] text-[var(--nexus-text-muted)]">
                      {formatTime(entry.timestamp)}
                    </span>
                    {entry.confidence > 0 && (
                      <span className="font-mono text-[10px] text-[var(--nexus-text-muted)]">
                        {entry.confidence.toFixed(0)}%
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-[var(--nexus-text-secondary)] leading-relaxed">
                    {entry.action}
                  </p>
                  {entry.ticket_id && (
                    <span className="inline-block mt-1 font-mono text-[10px] px-1.5 py-0.5 rounded bg-[var(--nexus-bg-elevated)] text-[var(--nexus-primary-bright)] border border-[var(--nexus-border)]">
                      {entry.ticket_id}
                    </span>
                  )}
                </div>
                {entry.duration_ms > 0 && (
                  <span className="font-mono text-[10px] text-[var(--nexus-text-muted)] tabular-nums flex-shrink-0">
                    {entry.duration_ms < 1000 ? `${entry.duration_ms}ms` : `${(entry.duration_ms / 1000).toFixed(1)}s`}
                  </span>
                )}
              </div>
            </div>
          ))}
          {activityFeed.length === 0 && (
            <div className="text-center py-8 text-[var(--nexus-text-muted)] text-xs">
              Waiting for agent activity...
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
